export function initNav(): void {
  const toggle = document.querySelector<HTMLButtonElement>('[data-menu-toggle]');
  const backdrop = document.querySelector<HTMLElement>('[data-menu-backdrop]');

  const setOpen = (open: boolean) => {
    document.body.classList.toggle('nav-open', open);
    toggle?.setAttribute('aria-expanded', String(open));
    if (backdrop) backdrop.hidden = !open;
  };

  toggle?.addEventListener('click', () => setOpen(!document.body.classList.contains('nav-open')));
  backdrop?.addEventListener('click', () => setOpen(false));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false);
  });

  revealCurrentNavEntry();
}

/**
 * Scrolls the sidebar so the entry for the page you just opened is on screen.
 *
 * Every page marks its own entry with `aria-current="page"`, but not in the same place:
 * an operation page marks the `.nav-link`, while a tag page marks the anchor inside the
 * group heading. Matching on the attribute covers both, where matching on
 * `.nav-link.is-current` silently did nothing for tag pages.
 *
 * Measurement is done with rects rather than `offsetTop`, which is relative to the
 * nearest positioned ancestor and not necessarily the scroller.
 */
function revealCurrentNavEntry(): void {
  const scroller = document.querySelector<HTMLElement>('[data-nav-scroll]');
  const current = scroller?.querySelector<HTMLElement>('[aria-current="page"]');
  if (!scroller || !current) return;

  const view = scroller.getBoundingClientRect();
  const target = current.getBoundingClientRect();

  // Already fully in view: leave the scroll position alone rather than jumping.
  if (target.top >= view.top && target.bottom <= view.bottom) return;

  const delta = target.top - view.top - (view.height - target.height) / 2;
  const max = scroller.scrollHeight - scroller.clientHeight;
  scroller.scrollTop = Math.min(Math.max(0, scroller.scrollTop + delta), max);
}

/** Highlights the table-of-contents entry matching the section currently on screen. */
export function initScrollSpy(): void {
  const links = [...document.querySelectorAll<HTMLAnchorElement>('.toc a')];
  if (!links.length) return;

  const byId = new Map<string, HTMLAnchorElement>();
  const targets: HTMLElement[] = [];

  for (const link of links) {
    const id = decodeURIComponent(link.hash.slice(1));
    const target = id ? document.getElementById(id) : null;
    if (target) {
      byId.set(id, link);
      targets.push(target);
    }
  }
  if (!targets.length) return;

  const visible = new Set<string>();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target.id);
        else visible.delete(entry.target.id);
      }
      const firstVisible = targets.find((t) => visible.has(t.id));
      for (const [id, link] of byId) {
        link.classList.toggle('is-active', firstVisible?.id === id);
      }
    },
    { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
  );

  targets.forEach((target) => observer.observe(target));
}

/**
 * 404 page behaviour.
 *
 * The page is a static file served for every unmatched URL, so the path the reader
 * actually asked for is only knowable in the browser. Filling it in turns a generic
 * "not found" into a specific one, and the search button puts the most likely next
 * action one click away instead of leaving them to find the box in the masthead.
 */
export function initNotFound(): void {
  const slot = document.querySelector<HTMLElement>('[data-notfound-path]');
  if (slot) slot.textContent = window.location.pathname + window.location.search;

  const trigger = document.querySelector<HTMLButtonElement>('[data-notfound-search]');
  trigger?.addEventListener('click', () => {
    const input = document.querySelector<HTMLInputElement>('[data-search-input]');
    if (!input) return;
    input.focus();
    input.select();
  });
}
